import {
  type BranchKey,
  STANDARD_BRANCH,
  type WorkspaceKey,
} from '@festina-life/api'
import {
  assertNotEmpty,
  type ConfigurableServer,
  getObjectEntries,
  type Optional,
} from '@festina-life/core'
import { RawTextInput } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { paperStyles, zIndexStyles } from '@festina-life/ui/libs'
import {
  Tree,
  TreeButtons,
  type TreeNodeData,
} from '@festina-life/ui/listsAndTables'
import { Icon } from '@festina-life/ui/structure'
import cx from 'classnames'
import { type ChangeEvent, useMemo, useState } from 'react'

import { useAllConciseBranches } from '../../../uiModels/branches/useAllConciseBranches'
import { useIsPersonaEditor } from '../../../uiModels/useIsPersonaEditor'
import { useWorkspaceManagerContext } from '../../../workspace/workspaceManagerContext'
import { useBranchCategoryTranslations } from '../../branchCreator/useBranchCategoryTranslations'
import { ToggleIconButton } from '../../toggleIconButton/ToggleIconButton'
import type {
  BranchLeaf,
  BranchTreeElementKey,
  ConciseBranchTreeNode,
} from '../types'
import { BranchesTreeLeaf } from './BranchesTreeLeaf'
import { BranchesTreeNode } from './BranchesTreeNode'
import {
  createBaseBranchHierarchy,
  createBranchHierarchy,
  createFolderHierarcy,
  generateTreeNodesForServer,
  getBranchServerFromPath,
} from './helpers'
import { useConciseBranchFromPath } from './useConciseBranchFromPath'

const BRANCHES_TREE_ID = 'branches-tree'

type Props = {
  onSelect: (elementKey: BranchTreeElementKey) => void
  activeElementKey?: Optional<BranchTreeElementKey>
}

export const BranchesTree = ({ onSelect, activeElementKey }: Props) => {
  const translate = useTranslate()
  const [branchQuery, setBranchQuery] = useState('')
  const [isGroupedByBranchName, setIsGroupedByBranchName] = useState(true)
  const conciseBranches = useAllConciseBranches()
  const branchCategoryTranslations = useBranchCategoryTranslations()
  const { conciseWorkspaces } = useWorkspaceManagerContext()
  const getConciseBranchFromPath = useConciseBranchFromPath()
  const isPersonaEditor = useIsPersonaEditor()

  const allSpecificationsMap = useMemo(
    () =>
      Object.fromEntries(
        getObjectEntries(conciseBranches).map(
          ([branchServer, serverBranches]) => {
            const serverWorkspaces = isPersonaEditor
              ? (conciseWorkspaces[branchServer] ?? [])
              : []

            return [
              branchServer,
              createBranchHierarchy({
                serverBranches: serverBranches ?? [],
                serverWorkspaces,
                createHierarcy: isGroupedByBranchName
                  ? createFolderHierarcy
                  : createBaseBranchHierarchy,
              }),
            ]
          },
        ),
      ) as Record<ConfigurableServer, Map<string, ConciseBranchTreeNode>>,
    [
      conciseBranches,
      conciseWorkspaces,
      isPersonaEditor,
      isGroupedByBranchName,
    ],
  )

  const specificationTreesPerServer = useMemo(
    () =>
      getObjectEntries(allSpecificationsMap).map(
        ([branchServer, serverSpecificationsMap]) => {
          const rootNodes = Array.from(serverSpecificationsMap.values()).filter(
            (specificationNode) => specificationNode.isRoot,
          )

          return generateTreeNodesForServer({
            conciseBranchNodes: rootNodes,
            activeElementKey,
            server: branchServer,
            branchCategoryTranslations,
            query: branchQuery.toLocaleLowerCase(),
            standardBranchTranslation: translate('branch.default'),
          })
        },
      ),
    [
      activeElementKey,
      allSpecificationsMap,
      branchCategoryTranslations,
      branchQuery,
      translate,
    ],
  )

  const handleBranchQueryChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setBranchQuery(event.target.value)
  }

  const handleNodeSelected = (node: TreeNodeData<BranchLeaf>) => {
    if (node.path.length > 1) {
      const branchServer = getBranchServerFromPath(node.path)
      return onSelect({ server: branchServer, name: node.name }) // Nodes will always be branches
    } else {
      onSelect(node.name as ConfigurableServer)
    }
  }

  const handleLeafSelected = (leaf: BranchLeaf) => {
    const branchServer = getBranchServerFromPath(leaf.path)

    if (!leaf.isStandardBranch && leaf.isWorkspace) {
      assertNotEmpty(leaf.data.name)
      assertNotEmpty(leaf.data.baseBranchName)

      onSelect({
        name: leaf.data.name,
        branchKey: { name: leaf.data.baseBranchName, server: branchServer },
      } satisfies WorkspaceKey)
    } else {
      onSelect({
        server: branchServer,
        name: leaf.isStandardBranch ? STANDARD_BRANCH : (leaf.data.name ?? ''),
      } satisfies BranchKey)
    }
  }

  const getLabel = ({ name, path }: { name: string; path: string[] }) => {
    if (!isGroupedByBranchName) {
      return undefined
    }
    const branchServer = getBranchServerFromPath(path)

    const branchNodeSpecification = allSpecificationsMap[branchServer].get(name)

    const nodeParentName = branchNodeSpecification?.parentName

    return nodeParentName ? name.substring(nodeParentName.length + 1) : name
  }

  return (
    <div className="size-full overflow-auto">
      <div
        className={cx(
          'flex items-center p-1 gap-x-2 sticky top-0',
          paperStyles(),
          zIndexStyles('sticky'),
        )}
      >
        <RawTextInput
          name="branchQuery"
          value={branchQuery}
          onChange={handleBranchQueryChanged}
          addOnProps={{ element: <Icon name="MagnifyingGlass" /> }}
        />
        <TreeButtons
          nodes={specificationTreesPerServer}
          id={BRANCHES_TREE_ID}
        />
        <ToggleIconButton
          tip={
            isGroupedByBranchName
              ? translate('branchesTree.groupByBaseBranch')
              : translate('branchesTree.groupByBranchName')
          }
          iconSize="sm"
          iconName="BarsArrowDown"
          isActive={isGroupedByBranchName}
          onClick={() => setIsGroupedByBranchName(!isGroupedByBranchName)}
        />
      </div>
      <div className="px-1">
        <Tree
          id={BRANCHES_TREE_ID}
          query={branchQuery}
          nodes={specificationTreesPerServer}
          renderLeaf={(leaf) => (
            <BranchesTreeLeaf
              leaf={leaf}
              conciseBranch={getConciseBranchFromPath({
                fullPath: leaf.path,
                isWorkspacePath: !leaf.isStandardBranch && leaf.isWorkspace,
              })}
              label={
                leaf.isStandardBranch || leaf.isWorkspace || !leaf.data.name
                  ? undefined
                  : getLabel({ name: leaf.data.name, path: leaf.path })
              }
            />
          )}
          renderNode={(node) => (
            <BranchesTreeNode
              node={node}
              label={getLabel({ name: node.name, path: node.path })}
            />
          )}
          nodeClassName="py-1"
          leafClassName="py-1"
          onSelectNode={handleNodeSelected}
          onSelectLeaf={handleLeafSelected}
          shouldSortNodes={false}
        />
      </div>
    </div>
  )
}
